import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_SYSTEM_COMPONENTS_RESOURCE } from 'src/features/systems/api/systemComponents/useListSystemComponents';
import { useUpdateComponent } from 'src/features/systems/api/systemComponents/useUpdateSystemComponent';
import SystemComponentFormModal from 'src/features/systems/components/systemComponents/form/SystemComponentFormModal';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { SystemComponent } from 'src/features/systems/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface SystemComponentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSystemComponent: SystemComponent;
    children?: React.ReactNode;
}

function UpdateSystemComponentFormModal<TData>({
    className,
    semantics,
    children,
    selectedSystemComponent,
}: SystemComponentFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateComponent({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SYSTEM_COMPONENTS_RESOURCE] });
            toast({
                title: formatMessage(systemsMessages.updateComponentSuccess),
                description: formatMessage(systemsMessages.updateComponentSuccessDescription, {
                    entry: name,
                }),
            });
        },
    });

    return (
        <SystemComponentFormModal
            selectedSystemComponent={selectedSystemComponent}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, componentId: selectedSystemComponent.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(systemsMessages.editComponentSubtitle, {
                entry: selectedSystemComponent.name,
            })}
        >
            {children}
        </SystemComponentFormModal>
    );
}

export default UpdateSystemComponentFormModal;
